/**
 * ApiHealthService — RESIL-M1
 *
 * Lee el resultado del sondeo de APIs upstream desde R2 (health/apis.json).
 * Caché en memoria con TTL de 30 min: evita peticiones repetidas al Worker
 * cada vez que el componente monta o el usuario navega entre pasos.
 *
 * @version 1.0.0
 */

import { WORKER_BASE as WORKER_BASE_CENTRAL } from '@/lib/config/workerEndpoints'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ApiEndpointHealth {
  name: string
  url: string
  ok: boolean
  status: number
  latencyMs: number
  critical: boolean
  error?: string
}

export interface ApiHealthResult {
  ts: string
  endpoints: ApiEndpointHealth[]
  hasCriticalIssues: boolean
}

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * URL base del Worker R2 (alias local).
 *
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 * Se preserva el nombre interno `WORKER_BASE` para minimizar churn en
 * las URLs derivadas (`HEALTH_APIS_URL`, `HEALTH_DRIFT_URL`).
 */
const WORKER_BASE = WORKER_BASE_CENTRAL
const HEALTH_APIS_URL = `${WORKER_BASE}/health/apis`
const CACHE_TTL_MS = 30 * 60 * 1000 // 30 minutos

// ─── In-memory cache ──────────────────────────────────────────────────────────

let _cache: { result: ApiHealthResult; fetchedAt: number } | null = null

// ─── Service ──────────────────────────────────────────────────────────────────

/**
 * Obtiene el estado de salud de las APIs upstream.
 * Devuelve el resultado cacheado si tiene menos de 30 min.
 * En caso de error de red, devuelve `null` (graceful degradation — sin banner).
 */
export async function fetchApiHealth(): Promise<ApiHealthResult | null> {
  const now = Date.now()

  if (_cache && now - _cache.fetchedAt < CACHE_TTL_MS) {
    return _cache.result
  }

  try {
    const res = await fetch(HEALTH_APIS_URL, { signal: AbortSignal.timeout(8000) })
    if (!res.ok) return null
    const result: ApiHealthResult = await res.json()
    _cache = { result, fetchedAt: now }
    return result
  } catch {
    // Worker no disponible o timeout → no mostrar banner (graceful)
    return null
  }
}

/** Invalida la caché manualmente (útil en tests o refresh forzado). */
export function invalidateApiHealthCache(): void {
  _cache = null
}

// ─── Endpoint Drift (RESIL-WFS-S0) ───────────────────────────────────────────

export interface DriftEventRaw {
  type: string
  endpointId: string
  url?: string
  fromUrl?: string
  toUrl?: string
  layerMap?: unknown
  added?: string[]
  removed?: string[]
  ptelRelevant: boolean
  detectedAt: string
}

export interface EndpointDriftResult {
  ts: string
  driftDetected: boolean
  events: DriftEventRaw[]
}

const HEALTH_DRIFT_URL = `${WORKER_BASE}/health/endpoint-drift`
let _driftCache: { result: EndpointDriftResult; fetchedAt: number } | null = null

/**
 * Obtiene el último resultado de drift del Worker.
 * Caché independiente con TTL de 30 min.
 * Devuelve `null` en caso de error (graceful degradation).
 */
export async function fetchEndpointDrift(): Promise<EndpointDriftResult | null> {
  const now = Date.now()
  if (_driftCache && now - _driftCache.fetchedAt < CACHE_TTL_MS) {
    return _driftCache.result
  }
  try {
    const res = await fetch(HEALTH_DRIFT_URL, { signal: AbortSignal.timeout(8000) })
    if (!res.ok) return null
    const result: EndpointDriftResult = await res.json()
    _driftCache = { result, fetchedAt: now }
    return result
  } catch {
    return null
  }
}

/** Invalida la caché de drift manualmente. */
export function invalidateDriftCache(): void {
  _driftCache = null
}
